import React, { useState, useEffect, useRef } from "react";
import { SunIcon, MoonIcon } from "@heroicons/react/24/outline";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { useChat } from "../context/ChatContext";
import { getInitials, CATEGORIES, SERVICES } from "../utils/helpers";
import logo from "../assets/logo.svg";
import logoDark from "../assets/logo-dark.svg";

const Navbar = () => {
  const { user, logout, isAuthenticated, isAdmin } = useAuth();
  const { unreadCount } = useChat() || { unreadCount: 0 };
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [itemsOpen, setItemsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const itemsRef = useRef(null);
  const servicesRef = useRef(null);
  const [darkMode, setDarkMode] = useState(
    () => localStorage.getItem("theme") === "dark",
  );
  const profileRef = useRef(null);
  // Dark mode effect
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [darkMode]);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target))
        setProfileOpen(false);
      if (itemsRef.current && !itemsRef.current.contains(e.target))
        setItemsOpen(false);
      if (servicesRef.current && !servicesRef.current.contains(e.target))
        setServicesOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim())
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
  };

  const navLinks = [
    { label: "Buy", to: "/listings" },
    { label: "Sell", to: "/post-ad", highlight: true },
    { label: "Deals", to: "/deals" },
    { label: "Blog", to: "/blog", icon: "📰" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? "bg-white/95 dark:bg-dark-bg/95 backdrop-blur-md shadow-md" : "bg-white dark:bg-dark-bg"}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img
              src={logo}
              alt="MyBazaarX"
              className="h-9 w-auto dark:hidden"
            />
            <img
              src={logoDark}
              alt="MyBazaarX"
              className="h-9 w-auto hidden dark:block"
            />
          </Link>

          {/* Search Bar */}
          <button
            aria-label="Toggle dark mode"
            className="ml-2 p-2 rounded-full hover:bg-primary-100 dark:hover:bg-dark-card transition-colors"
            onClick={() => setDarkMode((d) => !d)}
          >
            {darkMode ? (
              <SunIcon className="w-6 h-6 text-primary-600" />
            ) : (
              <MoonIcon className="w-6 h-6 text-primary-600" />
            )}
          </button>
          <form
            onSubmit={handleSearch}
            className="hidden md:flex flex-1 max-w-xl mx-6"
          >
            <div className="relative w-full">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search anything — mobiles, cars, flats..."
                className="w-full pl-10 pr-12 py-2.5 bg-gray-100 rounded-xl border-2 border-transparent
                           focus:bg-white focus:border-primary-400 focus:outline-none transition-all text-sm"
              />
              <button
                type="submit"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <span className="text-xs font-semibold text-primary-600 bg-primary-50 px-2 py-1 rounded-lg">
                  Search
                </span>
              </button>
            </div>
          </form>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-2">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                className={
                  link.highlight
                    ? "btn-primary py-2 px-5 text-sm"
                    : "flex items-center gap-1 px-4 py-2 text-sm font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all"
                }
              >
                {link.icon && <span>{link.icon}</span>}
                {link.label}
              </Link>
            ))}

            {/* All Items Dropdown */}
            <div ref={itemsRef} className="relative">
              <button
                onClick={() => {
                  setItemsOpen((o) => !o);
                  setServicesOpen(false);
                }}
                className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all"
              >
                All Items
                <svg
                  className={`w-3.5 h-3.5 transition-transform ${itemsOpen ? "rotate-180" : ""}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              <AnimatePresence>
                {itemsOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="absolute left-0 mt-2 w-56 bg-white rounded-2xl shadow-card-hover border border-gray-100 py-2 z-50 max-h-80 overflow-y-auto"
                  >
                    <Link
                      to="/listings"
                      onClick={() => setItemsOpen(false)}
                      className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-700 font-semibold"
                    >
                      🛍️ All Categories
                    </Link>
                    <hr className="my-1 border-gray-100" />
                    {CATEGORIES.map((cat) => (
                      <Link
                        key={cat.id}
                        to={`/listings?category=${cat.id}`}
                        onClick={() => setItemsOpen(false)}
                        className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                      >
                        <span>{cat.icon}</span> {cat.label}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* All Services Dropdown */}
            <div ref={servicesRef} className="relative">
              <button
                onClick={() => {
                  setServicesOpen((o) => !o);
                  setItemsOpen(false);
                }}
                className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-gray-700 hover:text-cyan-600 hover:bg-cyan-50 rounded-xl transition-all"
              >
                All Services
                <svg
                  className={`w-3.5 h-3.5 transition-transform ${servicesOpen ? "rotate-180" : ""}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              <AnimatePresence>
                {servicesOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="absolute left-0 mt-2 w-56 bg-white rounded-2xl shadow-card-hover border border-gray-100 py-2 z-50 max-h-80 overflow-y-auto"
                  >
                    <Link
                      to="/listings?category=services"
                      onClick={() => setServicesOpen(false)}
                      className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-cyan-50 hover:text-cyan-700 font-semibold"
                    >
                      🛠️ All Services
                    </Link>
                    <Link
                      to="/careers"
                      onClick={() => setServicesOpen(false)}
                      className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-cyan-50 hover:text-cyan-700 font-semibold"
                    >
                      💼 Jobs & Careers
                    </Link>
                    <hr className="my-1 border-gray-100" />
                    {SERVICES.map((svc) => (
                      <Link
                        key={svc.id}
                        to={`/listings?category=services&service=${svc.id}`}
                        onClick={() => setServicesOpen(false)}
                        className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-cyan-50 hover:text-cyan-700 transition-colors"
                      >
                        <span>{svc.icon}</span> {svc.label}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {isAuthenticated ? (
              <div className="flex items-center gap-2 ml-2">
                {/* Chat */}
                <Link
                  to="/chat"
                  className="relative p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 w-5 bg-accent-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </Link>

                {/* Profile Dropdown */}
                <div ref={profileRef} className="relative">
                  <button
                    onClick={() => setProfileOpen((p) => !p)}
                    className="flex items-center gap-2 p-1 rounded-xl hover:bg-gray-100 transition-all"
                  >
                    {user?.avatar ? (
                      <img
                        src={user.avatar}
                        alt={user.name}
                        className="w-8 h-8 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-accent-500 rounded-lg flex items-center justify-center text-white text-sm font-bold">
                        {getInitials(user?.name)}
                      </div>
                    )}
                    <svg
                      className="w-4 h-4 text-gray-500"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  <AnimatePresence>
                    {profileOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 8, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.95 }}
                        transition={{ duration: 0.15 }}
                        className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-card-hover border border-gray-100 py-2 z-50"
                      >
                        <div className="px-4 py-3 border-b border-gray-100">
                          <p className="font-semibold text-gray-900 text-sm truncate">
                            {user?.name}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {user?.email}
                          </p>
                        </div>
                        {[
                          {
                            label: "My Profile",
                            to: `/profile/${user?._id || user?.id}`,
                            icon: "👤",
                          },
                          { label: "My Ads", to: "/my-ads", icon: "📋" },
                          {
                            label: "My Jobs Applications",
                            to: "/careers",
                            icon: "💼",
                          },
                          { label: "Wishlist", to: "/wishlist", icon: "❤️" },
                          { label: "Messages", to: "/chat", icon: "💬" },
                          ...(isAdmin
                            ? [
                                {
                                  label: "Admin Panel",
                                  to: "/admin",
                                  icon: "⚙️",
                                },
                              ]
                            : []),
                        ].map((item) => (
                          <Link
                            key={item.label}
                            to={item.to}
                            onClick={() => setProfileOpen(false)}
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                          >
                            <span>{item.icon}</span> {item.label}
                          </Link>
                        ))}
                        <hr className="my-1 border-gray-100" />
                        <button
                          onClick={() => {
                            logout();
                            setProfileOpen(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <span>🚪</span> Logout
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 ml-2">
                <Link to="/login" className="btn-secondary py-2 px-4 text-sm">
                  Login
                </Link>
                <Link to="/register" className="btn-primary py-2 px-4 text-sm">
                  Sign Up
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileOpen((p) => !p)}
            className="md:hidden p-2 rounded-xl text-gray-600 hover:bg-gray-100 transition-all"
          >
            {mobileOpen ? (
              <svg
                className="w-6 h-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            ) : (
              <svg
                className="w-6 h-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile Search */}
        <div className="md:hidden pb-3">
          <form onSubmit={handleSearch}>
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search anything..."
                className="w-full pl-10 pr-4 py-2.5 bg-gray-100 rounded-xl border-2 border-transparent focus:bg-white focus:border-primary-400 focus:outline-none text-sm"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-gray-100 shadow-lg"
          >
            <div className="px-4 py-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.label}
                  to={link.to}
                  className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all ${link.highlight ? "bg-primary-600 text-white" : "text-gray-700 hover:bg-primary-50"}`}
                >
                  {link.icon && <span>{link.icon}</span>}
                  {link.label}
                </Link>
              ))}
              <div>
                <p className="px-4 pt-2 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                  Browse Items
                </p>
                <div className="flex flex-wrap gap-2 px-2">
                  {CATEGORIES.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/listings?category=${cat.id}`}
                      className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 rounded-lg text-xs text-gray-700 hover:bg-primary-50"
                    >
                      {cat.icon} {cat.label}
                    </Link>
                  ))}
                </div>
              </div>
              <div>
                <p className="px-4 pt-2 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                  Browse Services
                </p>
                <div className="flex flex-wrap gap-2 px-2">
                  {SERVICES.map((svc) => (
                    <Link
                      key={svc.id}
                      to={`/listings?category=services&service=${svc.id}`}
                      className="flex items-center gap-1 px-3 py-1.5 bg-cyan-50 rounded-lg text-xs text-gray-700 hover:bg-cyan-100"
                    >
                      {svc.icon} {svc.label}
                    </Link>
                  ))}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 px-2 pt-1">
                <Link
                  to="/help-center"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 rounded-lg text-xs text-blue-700 hover:bg-blue-100 font-medium"
                >
                  🆘 Help Center
                </Link>
                <Link
                  to="/careers"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-1 px-3 py-1.5 bg-orange-50 rounded-lg text-xs text-orange-700 hover:bg-orange-100 font-medium"
                >
                  💼 Careers
                </Link>
                <Link
                  to="/about"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 rounded-lg text-xs text-gray-700 hover:bg-gray-200 font-medium"
                >
                  ℹ️ About Us
                </Link>
                <Link
                  to="/report-problem"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-1 px-3 py-1.5 bg-red-50 rounded-lg text-xs text-red-700 hover:bg-red-100 font-medium"
                >
                  🚨 Report
                </Link>
              </div>

              {isAuthenticated ? (
                <>
                  <Link
                    to={`/profile/${user?._id || user?.id}`}
                    className="block px-4 py-3 text-sm text-gray-700 hover:bg-primary-50 rounded-xl"
                  >
                    My Profile
                  </Link>
                  <Link
                    to="/my-ads"
                    className="block px-4 py-3 text-sm text-gray-700 hover:bg-primary-50 rounded-xl"
                  >
                    My Ads
                  </Link>
                  <Link
                    to="/careers"
                    className="block px-4 py-3 text-sm text-gray-700 hover:bg-primary-50 rounded-xl"
                  >
                    My Jobs Applications
                  </Link>
                  <Link
                    to="/chat"
                    className="block px-4 py-3 text-sm text-gray-700 hover:bg-primary-50 rounded-xl"
                  >
                    Messages{" "}
                    {unreadCount > 0 && (
                      <span className="ml-2 badge bg-accent-500 text-white">
                        {unreadCount}
                      </span>
                    )}
                  </Link>
                  <button
                    onClick={logout}
                    className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 rounded-xl"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <div className="flex gap-2 pt-2">
                  <Link
                    to="/login"
                    className="flex-1 btn-secondary text-center py-2.5 text-sm"
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className="flex-1 btn-primary text-center py-2.5 text-sm"
                  >
                    Sign Up
                  </Link>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
